import axios from "src/lib/axios";
import { getParams } from "src/common/api";

// Moods

/**
 *
 * @param {Object} options - query options
 * @param {ObjectId} options.room - Room object id
 * @param {String} options.keyword - Search keyword
 * @param {Number} options.page - Page number
 * @param {Number} options.page_size - Number of items per page
 * @param {String} options.sortBy - Field to sort by
 * @param {String} options.sortOrder - Sort order
 * @param {Number} options.limit - Number of items to return
 * @returns
 */
export const getAllMoodsByAdmin = async ({
  room,
  keyword = null,
  page = 0,
  page_size = 10,
  sortBy,
  sortOrder,
  limit,
}) => {
  const params = getParams({
    room,
    keyword,
    page,
    page_size,
    sortBy,
    sortOrder,
    limit,
  });
  const { data: response } = await axios.get(
    `/moods/admin-find-all-mood?${params}`,
  );
  return response.data;
};

/**
 *
 * @param {Object} options - query options
 * @param {ObjectId[]} options.rooms - Array of room object ids
 * @param {ObjectId[]} options.styles - Array of style object ids
 * @param {String} options.keyword - Search keyword
 * @param {Number} options.page - Page number
 * @param {Number} options.page_size - Number of items per page
 * @param {String} options.sortBy - Field to sort by
 * @param {String} options.sortOrder - Sort order
 * @param {Number} options.limit - Number of items to return
 * @returns
 */
export const getAllMoodsByUser = async ({
  rooms,
  styles,
  keyword = null,
  page = null,
  page_size = null,
  sortBy,
  sortOrder,
  limit,
}) => {
  const params = getParams({
    rooms,
    styles,
    keyword,
    page,
    page_size,
    sortBy,
    sortOrder,
    limit,
  });
  const { data: response } = await axios.get(
    `/moods/user-find-all-mood?${params}`,
  );
  return response.data;
};

/**
 *
 * @param {ObjectId} id - Mood object id
 * @returns
 */
export const getMoodDetail = async (id) => {
  const { data: response } = await axios.get(`/moods/mood-detail/${id}`);
  return response.data;
};

/**
 * @param {ObjectId} id - Mood object id
 *
 */
export const deleteMood = async (id) => {
  const { data: response } = await axios.delete(`/moods/delete-mood/${id}`);
  return response.data;
};

/**
 * @param {ObjectId} id - Mood object id
 *
 */
export const togglePublicMood = async (id, isPublic) => {
  const { data: response } = await axios.patch(`/moods/public-mood/${id}`, {
    is_public: isPublic,
  });
  return response.data;
};

/**
 *
 * @param {Object} data
 * @param {String} data.mood_name
 * @param {Object} data.room
 * @param {Object[]} data.styles
 * @param {ObjectId[]} data.product_choice_moods
 * @param {Object} data.media
 * @param {Object[]} data.media.hotspot
 * @param {File} data.image
 * @param {Object[]} data.hashtags
 * @returns
 */
export const createMood = async (data) => {
  const { data: response } = await axios.post(`/moods/create-mood`, data, {
    headers: {
      "Content-Type": "multipart/form-data", // do not forget this
    },
  });
  return response.data;
};

/**
 * @param {ObjectId} id - Mood object id
 * @param {Object} data
 * @param {String} data.mood_name
 * @param {Object} data.room
 * @param {Object[]} data.styles
 * @param {ObjectId[]} data.product_choice_moods
 * @param {Object} data.media
 * @param {Object[]} data.media.hotspot
 * @param {File} data.image
 * @param {Object[]} data.hashtags
 * @returns
 */
export const updateMood = async (id, data) => {
  const { data: response } = await axios.patch(
    `/moods/update-mood/${id}`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data", // do not forget this
      },
    },
  );
  return response.data;
};

// Mood Styles

/**
 *
 * @returns
 */
export const getAllStyles = async () => {
  const { data: response } = await axios.get(`/moods/find-all-style`);
  return response.data;
};

/**
 *
 * @param {Object} data - The data for the new style.
 * @param {string} data.name - The name of the new style.
 * @returns
 */
export const createStyle = async (data) => {
  const { data: response } = await axios.post(`/moods/create-style`, data);
  return response.data;
};

// Mood Rooms

/**
 *
 * @param {Object} data
 * @param {String} data.room_th
 * @param {String} data.room_en
 * @param {String} data.room_cn
 * @param {File} data.cover_image
 * @returns
 */
export const createRoom = async (data) => {
  const { data: response } = await axios.post(`/moods/create-room`, data);
  return response.data;
};

export const getAllRooms = async () => {
  const { data: response } = await axios.get(`/moods/find-all-room`);
  return response.data;
};

export const getRoomDetail = async (id) => {
  const { data: response } = await axios.get(`/moods/room-detail/${id}`);
  return response.data;
};

/**
 *
 * @param {ObjectId} id
 * @param {Object} data
 * @param {String} data.room_th
 * @param {String} data.room_en
 * @param {String} data.room_cn
 * @param {File} data.cover_image
 * @param {Number} data.position
 * @param {Boolean} data.is_public
 * @returns
 */
export const updateRoom = async (id, data) => {
  const { data: response } = await axios.patch(
    `/moods/update-room/${id}`,
    data,
  );
  return response.data;
};

/**
 *
 * @param {Object[]} data - new rearranged room positions
 * @returns
 */
export const moveRoomPosition = async (data) => {
  const { data: response } = await axios.patch(`/moods/move-room`, data);
  return response.data;
};

export const togglePublicRoom = async (id, isPublic) => {
  const { data: response } = await axios.patch(`/moods/public-room/${id}`, {
    is_public: isPublic,
  });
  return response.data;
};

export const deleteRoom = async (id) => {
  const { data: response } = await axios.delete(`/moods/delete-room/${id}`);
  return response.data;
};

// Mood Banner

/**
 *
 * @param {Object} data
 * @param {File} data.image
 * @returns
 */
export const createBanner = async (data) => {
  const { data: response } = await axios.post(`/moods/create-banner`, data, {
    headers: {
      "Content-Type": "multipart/form-data", // do not forget this
    },
  });
  return response.data;
};

export const getAllBanners = async () => {
  const { data: response } = await axios.get(`/moods/find-all-banner`);
  return response.data;
};

/**
 *
 * @param {Object[]} data - new rearranged banner positions
 * @returns
 */
export const moveBannerPosition = async (data) => {
  const { data: response } = await axios.patch(`/moods/move-banner`, data);
  return response.data;
};

export const togglePublicBanner = async (id, isPublic) => {
  const { data: response } = await axios.patch(`/moods/public-banner/${id}`, {
    is_public: isPublic,
  });
  return response.data;
};

export const deleteBanner = async (id) => {
  const { data: response } = await axios.delete(`/moods/delete-banner/${id}`);
  return response.data;
};
